/**
 * WEARING MIND - Main Theme JavaScript
 * Core functionality and utilities
 */

class WearingMindTheme {
  constructor() {
    this.init();
  }

  init() {
    this.setupMobileMenu();
    this.setupNewsletterPopup();
    this.setupStickyAddToCart();
    this.setupImageLazyLoad();
    this.setupQuantitySelectors();
  }

  // Mobile Menu Toggle
  setupMobileMenu() {
    const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    
    if (!menuToggle || !mobileMenu) return;

    menuToggle.addEventListener('click', () => {
      const isOpen = mobileMenu.getAttribute('aria-hidden') === 'false';
      mobileMenu.setAttribute('aria-hidden', !isOpen);
      menuToggle.setAttribute('aria-expanded', !isOpen);
      document.body.style.overflow = isOpen ? '' : 'hidden';
    });
  }

  // Newsletter Popup (non-aggressive)
  setupNewsletterPopup() {
    const popup = document.getElementById('newsletter-popup');
    if (!popup) return;

    const hasSeenPopup = sessionStorage.getItem('wm-newsletter-seen');
    
    if (!hasSeenPopup) {
      setTimeout(() => {
        popup.removeAttribute('hidden');
        popup.classList.add('is-visible');
        sessionStorage.setItem('wm-newsletter-seen', 'true');
      }, 30000); // Show after 30 seconds
    }

    // Close popup
    const closeBtn = popup.querySelector('[data-popup-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        popup.classList.remove('is-visible');
        setTimeout(() => popup.setAttribute('hidden', ''), 300);
      });
    }
  }

  // Sticky Add to Cart (Mobile Product Pages)
  setupStickyAddToCart() {
    const productForm = document.querySelector('[data-product-form]');
    const stickyCart = document.querySelector('[data-sticky-cart]');
    
    if (!productForm || !stickyCart) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        stickyCart.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(productForm);
  }

  // Progressive Image Loading
  setupImageLazyLoad() {
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');
    
    if ('loading' in HTMLImageElement.prototype) {
      return; // Browser supports native lazy loading
    }

    // Fallback for older browsers
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.classList.add('loaded');
          imageObserver.unobserve(img);
        }
      });
    });

    lazyImages.forEach(img => imageObserver.observe(img));
  }

  // Quantity Input Controls
  setupQuantitySelectors() {
    document.querySelectorAll('[data-quantity-wrapper]').forEach(wrapper => {
      const input = wrapper.querySelector('input[type="number"]');
      const decreaseBtn = wrapper.querySelector('[data-quantity-decrease]');
      const increaseBtn = wrapper.querySelector('[data-quantity-increase]');

      if (!input) return;

      decreaseBtn?.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 1;
        if (currentValue > 1) {
          input.value = currentValue - 1;
          input.dispatchEvent(new Event('change'));
        }
      });

      increaseBtn?.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 1;
        const max = parseInt(input.max) || 999;
        if (currentValue < max) {
          input.value = currentValue + 1;
          input.dispatchEvent(new Event('change'));
        }
      });
    });
  }
}

/**
 * Cart API Helper
 */
class CartAPI {
  static async addItem(id, quantity = 1, properties = {}) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity, properties })
      });
      
      if (!response.ok) throw new Error('Failed to add item');
      
      return await response.json();
    } catch (error) {
      console.error('Cart error:', error);
      throw error;
    }
  }

  static async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Cart fetch error:', error);
      throw error;
    }
  }

  static async updateItem(line, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      
      if (!response.ok) throw new Error('Failed to update item');
      
      return await response.json();
    } catch (error) {
      console.error('Cart update error:', error);
      throw error;
    }
  }
}

/**
 * Product Variant Selector
 */
class VariantSelector {
  constructor(form) {
    this.form = form;
    this.variantData = JSON.parse(form.querySelector('[data-variant-json]')?.textContent || '[]');
    this.setupListeners();
  }

  setupListeners() {
    this.form.querySelectorAll('input[name="option"]').forEach(input => {
      input.addEventListener('change', () => this.updateVariant());
    });
  }

  updateVariant() {
    const selectedOptions = Array.from(
      this.form.querySelectorAll('input[name="option"]:checked')
    ).map(input => input.value);

    const variant = this.variantData.find(v => {
      return selectedOptions.every((option, index) => v.options[index] === option);
    });

    if (variant) {
      this.form.querySelector('[name="id"]').value = variant.id;
      this.updatePrice(variant.price);
      this.updateAvailability(variant.available);
    }
  }

  updatePrice(price) {
    const priceElement = this.form.querySelector('[data-product-price]');
    if (priceElement) {
      priceElement.textContent = this.formatMoney(price);
    }
  }

  updateAvailability(available) {
    const button = this.form.querySelector('[data-add-to-cart]');
    if (button) {
      button.disabled = !available;
      button.textContent = available ? 'Ajouter au panier' : 'Rupture de stock';
    }
  }

  formatMoney(cents) {
    return (cents / 100).toFixed(2) + '�';
  }
}

// Initialize Theme
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new WearingMindTheme();
    
    // Initialize variant selectors on product forms
    document.querySelectorAll('[data-product-form]').forEach(form => {
      new VariantSelector(form);
    });
  });
} else {
  new WearingMindTheme();
}

// Export for global access
window.WearingMind = window.WearingMind || {};
window.WearingMind.CartAPI = CartAPI;
window.WearingMind.VariantSelector = VariantSelector;
